import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, PackageSearch } from "lucide-react";
import { get } from "@/lib/api";
import { fmtNum } from "@/lib/format";
import { Panel } from "@/components/common/Panel";
import { LoadingState, EmptyState } from "@/components/common/States";

const inputCls = "h-8 bg-input border border-border rounded-sm px-2 text-xs text-foreground w-full";

/** AUDIT INV V2 §6.7 — Écran Traçabilité (W7).
 *  Deux modes de recherche :
 *    1. Lot (par code OU par ID numérique) → traçabilité DESCENDANTE (production → mouvements → stock).
 *    2. Mouvement par ID → traçabilité ASCENDANTE (mouvement → lot → OF → produit + amont).
 *  Les deux appels backend conservent strictement l'isolation multi-tenant. */
export const TracabilitePanel = ({ lots }) => {
  const [mode, setMode] = useState("lot");                     // lot | mouvement
  const [lotRecherche, setLotRecherche] = useState("");
  const [mvtId, setMvtId] = useState("");

  // Chercher le lot par code OU par id si l'utilisateur a tapé un nombre
  const lotSelId = (() => {
    if (!lotRecherche) return null;
    if (/^\d+$/.test(lotRecherche)) return Number(lotRecherche);
    const match = (lots || []).find((l) => l.code?.toLowerCase() === lotRecherche.trim().toLowerCase());
    return match ? match.id : null;
  })();

  const traceLot = useQuery({
    queryKey: ["trace-lot", lotSelId],
    queryFn: () => get(`/inventaire/lots/${lotSelId}/tracabilite`),
    enabled: mode === "lot" && !!lotSelId,
  });

  const traceMvt = useQuery({
    queryKey: ["trace-mvt", mvtId],
    queryFn: () => get(`/inventaire/mouvements/${mvtId}`),
    enabled: mode === "mouvement" && !!mvtId && /^\d+$/.test(String(mvtId)),
  });

  return (
    <div data-testid="tracabilite-panel" className="space-y-3">
      <Panel title="Recherche traçabilité" testId="panel-tracabilite-recherche" bodyClassName="p-3">
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex gap-1">
            <button data-testid="trace-mode-lot" onClick={() => setMode("lot")}
              className={`h-8 px-3 text-xs rounded-sm font-semibold ${mode === "lot" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
              Par Lot (descendant)
            </button>
            <button data-testid="trace-mode-mvt" onClick={() => setMode("mouvement")}
              className={`h-8 px-3 text-xs rounded-sm font-semibold ${mode === "mouvement" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
              Par Mouvement (ascendant)
            </button>
          </div>
          {mode === "lot" ? (
            <div className="flex-1 min-w-[260px]">
              <label className="block text-[11px] font-mono text-muted-foreground">Code lot ou ID</label>
              <div className="relative mt-1">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                <input data-testid="trace-lot-input" list="trace-lot-list" className={`${inputCls} pl-7`}
                  value={lotRecherche} onChange={(e) => setLotRecherche(e.target.value)}
                  placeholder="LOT-20260921-CIM25-001 ou 42" />
                <datalist id="trace-lot-list">
                  {(lots || []).map((l) => <option key={l.id} value={l.code}>{l.produit}</option>)}
                </datalist>
              </div>
            </div>
          ) : (
            <div className="flex-1 min-w-[260px]">
              <label className="block text-[11px] font-mono text-muted-foreground">ID du mouvement</label>
              <input data-testid="trace-mvt-input" type="number" className={`${inputCls} mt-1`}
                value={mvtId} onChange={(e) => setMvtId(e.target.value)} placeholder="12345" />
            </div>
          )}
        </div>
      </Panel>

      {/* ============ Mode LOT (descendant) ============ */}
      {mode === "lot" && lotSelId && (traceLot.isLoading ? <LoadingState rows={6} /> :
        traceLot.error ? <EmptyState title="Lot introuvable" hint={traceLot.error.message} /> :
        traceLot.data && <LotTracabilite data={traceLot.data} />)}
      {mode === "lot" && lotRecherche && !lotSelId && (
        <EmptyState title="Code lot inconnu" hint="Vérifiez le code (ex. LOT-20260921-CIM25-001) ou saisissez l'ID numérique." />
      )}

      {/* ============ Mode MOUVEMENT (ascendant) ============ */}
      {mode === "mouvement" && mvtId && (traceMvt.isLoading ? <LoadingState rows={6} /> :
        traceMvt.error ? <EmptyState title="Mouvement introuvable" hint={traceMvt.error.message} /> :
        traceMvt.data && <MvtTracabilite data={traceMvt.data} />)}
    </div>
  );
};

const Row = ({ label, value, mono }) => (
  <div className="flex gap-2 text-xs py-0.5">
    <span className="text-muted-foreground w-32 shrink-0">{label}</span>
    <span className={mono ? "font-mono" : ""}>{value || "—"}</span>
  </div>
);

const LotTracabilite = ({ data }) => {
  const l = data.lot || {};
  return (
    <div className="space-y-3">
      <Panel title={`Lot ${l.code} — fiche`} testId="panel-trace-lot-fiche" bodyClassName="p-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <Row label="Produit" value={`${l.produit} — ${l.designation}`} mono />
          <Row label="OF source" value={l.of} mono />
          <Row label="Ligne" value={l.ligne} />
          <Row label="Statut lot" value={l.statut} />
          <Row label="Début production" value={l.debut_production} mono />
          <Row label="Fin production" value={l.fin_production} mono />
          <Row label="Quantité bonne" value={`${fmtNum(l.quantite_bonne)} ${l.unite}`} mono />
          <Row label="Rejets" value={`${fmtNum(l.rejets)} ${l.unite}`} mono />
          <Row label="Total disponible" value={`${fmtNum(data.total_disponible)} ${l.unite}`} mono />
        </div>
      </Panel>

      <Panel title="Stock actuel par emplacement" testId="panel-trace-stock" bodyClassName="p-3">
        {(data.stock_actuel_par_emplacement || []).length === 0 ? (
          <EmptyState compact title="Lot entièrement consommé ou expédié" />
        ) : (
          <div className="space-y-1 text-xs font-mono">
            {data.stock_actuel_par_emplacement.map((e, i) => (
              <div key={i} data-testid={`trace-stock-empl-${i}`} className="flex items-center gap-2 py-1 border-b border-border/40 last:border-0">
                <PackageSearch className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="flex-1">{e.entrepot} / {e.zone} / {e.emplacement}</span>
                <span className="font-semibold">{fmtNum(e.quantite)} {e.unite}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title={`Timeline des mouvements du lot (${(data.timeline || []).length})`} testId="panel-trace-timeline" bodyClassName="p-3">
        {(data.timeline || []).length === 0 ? <EmptyState compact title="Aucun mouvement" /> : (
          <div className="space-y-1 text-[11px] font-mono">
            {data.timeline.map((m, i) => (
              <div key={m.id} data-testid={`trace-timeline-${i}`} className="flex items-center gap-2 py-1 border-b border-border/40 last:border-0">
                <span className="text-muted-foreground w-32">{m.horodatage}</span>
                <span className={m.quantite > 0 ? "text-emerald-400 w-24 text-right" : "text-rose-400 w-24 text-right"}>
                  {m.quantite > 0 ? "+" : ""}{fmtNum(m.quantite)} {m.unite}
                </span>
                <span className="w-28 text-muted-foreground">{m.type}</span>
                <span className="flex-1">
                  {m.origine && <span>← {m.origine}</span>}{m.origine && m.destination && " "}
                  {m.destination && <span>→ {m.destination}</span>}
                </span>
                <span className="text-muted-foreground">{m.utilisateur}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
};

const MvtTracabilite = ({ data }) => {
  const m = data.mouvement || {};
  const lot = data.lot;
  const of = data.of;
  return (
    <div className="space-y-3">
      <Panel title={`Mouvement #${m.id}`} testId="panel-trace-mvt-fiche" bodyClassName="p-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <Row label="Horodatage" value={m.horodatage} mono />
          <Row label="Type" value={m.type} />
          <Row label="Quantité" value={`${m.quantite > 0 ? "+" : ""}${fmtNum(m.quantite)} ${m.unite || ""}`} mono />
          <Row label="Statut stock" value={m.statut_stock} />
          <Row label="Origine" value={m.origine} />
          <Row label="Destination" value={m.destination} />
          <Row label="Utilisateur" value={m.utilisateur} />
          <Row label="Motif" value={m.motif} />
          <Row label="Commentaire" value={m.commentaire} />
        </div>
      </Panel>

      {lot && (
        <Panel title="Lot d'origine" testId="panel-trace-mvt-lot" bodyClassName="p-3">
          <Row label="Lot" value={lot.code} mono />
          {of && <>
            <Row label="OF" value={of.numero} mono />
            <Row label="Démarré le" value={of.demarre_a} mono />
            <Row label="Clôturé le" value={of.cloture_a || "en cours"} mono />
          </>}
        </Panel>
      )}

      <Panel title={`Mouvements amont du même lot (${(data.mouvements_amont || []).length})`} testId="panel-trace-mvt-amont" bodyClassName="p-3">
        {(data.mouvements_amont || []).length === 0 ? <EmptyState compact title="Aucun antécédent" /> : (
          <div className="space-y-1 text-[11px] font-mono">
            {data.mouvements_amont.map((a) => (
              <div key={a.id} className="flex gap-2 py-1 border-b border-border/40 last:border-0">
                <span className="text-muted-foreground w-32">{a.horodatage}</span>
                <span className={a.quantite > 0 ? "text-emerald-400 w-24 text-right" : "text-rose-400 w-24 text-right"}>
                  {a.quantite > 0 ? "+" : ""}{fmtNum(a.quantite)} {a.unite || ""}
                </span>
                <span className="w-28 text-muted-foreground">{a.type}</span>
                <span className="flex-1">{a.destination || a.origine || ""}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
};
